import { useMemo, useState } from "react";
import { BookOpen, Brain } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "../stores/toast";
import {
  Button,
  Input,
  SearchSelect,
  Textarea,
  Badge,
  Toggle,
  SettingsCard,
  useConfirm,
} from "../components/ui";
import { DataTable, type Column, type DataTableRowActionItem, type DataTableRowActions } from "../components/DataTable";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

type ModelPurpose = "BO_COPILOT" | "FE_SUPPORT" | "EMBEDDING" | "VISION" | "IMAGE_GEN";

const MODEL_PURPOSE_OPTIONS: { value: ModelPurpose; label: string }[] = [
  { value: "BO_COPILOT", label: "Backoffice copilot" },
  { value: "FE_SUPPORT", label: "Frontend support" },
  { value: "EMBEDDING", label: "Embedding" },
  { value: "VISION", label: "Vision" },
  { value: "IMAGE_GEN", label: "Image generation" },
];

interface AiModel {
  id: string;
  name: string;
  baseUrl: string;
  apiKey: string;
  modelId: string;
  purpose: ModelPurpose;
  temperature: string;
  isDefault: boolean;
}

interface KbArticle {
  id: string;
  title: string;
  content: string;
  tags: string[];
  active: boolean;
  updatedAt: string;
}

interface ModelForm {
  name: string;
  baseUrl: string;
  apiKey: string;
  modelId: string;
  purpose: ModelPurpose;
  temperature: string;
  isDefault: boolean;
}

interface KbForm {
  title: string;
  content: string;
  tags: string;
  active: boolean;
}

const emptyModel = (): ModelForm => ({
  name: "",
  baseUrl: "https://api.openai.com/v1",
  apiKey: "",
  modelId: "gpt-4o-mini",
  purpose: "BO_COPILOT",
  temperature: "0.3",
  isDefault: false,
});

const emptyKb = (): KbForm => ({
  title: "",
  content: "",
  tags: "",
  active: true,
});

export function AiModels() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const [modelEditor, setModelEditor] = useState<AiModel | "new" | null>(null);
  const [modelForm, setModelForm] = useState<ModelForm>(emptyModel());
  const [kbEditor, setKbEditor] = useState<KbArticle | "new" | null>(null);
  const [kbForm, setKbForm] = useState<KbForm>(emptyKb());

  const { data: models, isLoading: modelsLoading } = useQuery({
    queryKey: ["ai-models"],
    queryFn: () => api<{ items: AiModel[] }>("/ai/models"),
  });

  const { data: kb, isLoading: kbLoading } = useQuery({
    queryKey: ["ai-kb"],
    queryFn: () => api<{ items: KbArticle[] }>("/ai/kb"),
  });

  const modelSaveMut = useMutation({
    mutationFn: (payload: ModelForm & { id?: string }) => {
      const body = {
        ...payload,
        temperature: Number(payload.temperature) || 0.3,
      };
      if (payload.id) return api(`/ai/models/${payload.id}`, { method: "PUT", body });
      return api<{ id: string }>("/ai/models", { method: "POST", body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-models"] });
      toast("success", modelEditor === "new" ? "Model created" : "Model updated");
      closeModelEditor();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const modelDeleteMut = useMutation({
    mutationFn: (id: string) => api(`/ai/models/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-models"] });
      toast("success", "Model deleted");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Delete failed"),
  });

  const modelTestMut = useMutation({
    mutationFn: (id: string) => api(`/ai/models/${id}/test`, { method: "POST" }),
    onSuccess: () => toast("success", "Connection OK"),
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Test failed"),
  });

  const kbSaveMut = useMutation({
    mutationFn: (payload: KbForm & { id?: string }) => {
      const body = { ...payload, tags: payload.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (payload.id) return api(`/ai/kb/${payload.id}`, { method: "PUT", body });
      return api<{ id: string }>("/ai/kb", { method: "POST", body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-kb"] });
      toast("success", kbEditor === "new" ? "Article created" : "Article updated");
      closeKbEditor();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const kbDeleteMut = useMutation({
    mutationFn: (id: string) => api(`/ai/kb/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-kb"] });
      toast("success", "Article deleted");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Delete failed"),
  });

  const reindexMut = useMutation({
    mutationFn: () => api<{ queued: number }>("/ai/reindex", { method: "POST" }),
    onSuccess: (res) => toast("success", `Queued ${res.queued} items for reindex`),
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Reindex failed"),
  });

  function openModelCreate() {
    setModelEditor("new");
    setModelForm(emptyModel());
  }

  function openModelEdit(m: AiModel) {
    setModelEditor(m);
    setModelForm({
      name: m.name,
      baseUrl: m.baseUrl,
      apiKey: m.apiKey,
      modelId: m.modelId,
      purpose: m.purpose,
      temperature: String(m.temperature),
      isDefault: m.isDefault,
    });
  }

  function closeModelEditor() {
    setModelEditor(null);
    setModelForm(emptyModel());
  }

  function openKbCreate() {
    setKbEditor("new");
    setKbForm(emptyKb());
  }

  function openKbEdit(a: KbArticle) {
    setKbEditor(a);
    setKbForm({
      title: a.title,
      content: a.content,
      tags: a.tags.join(", "),
      active: a.active,
    });
  }

  function closeKbEditor() {
    setKbEditor(null);
    setKbForm(emptyKb());
  }

  const modelColumns: Column<AiModel>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    {
      key: "purpose",
      header: "Purpose",
      sortable: true,
      sortValue: (r) => r.purpose,
      render: (r) => <Badge color="blue">{r.purpose}</Badge>,
    },
    {
      key: "modelId",
      header: "Model ID",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.modelId,
      render: (r) => <span className="font-mono text-xs">{r.modelId}</span>,
    },
    {
      key: "default",
      header: "Default",
      sortable: true,
      sortValue: (r) => r.isDefault,
      exportValue: (r) => (r.isDefault ? "Yes" : "No"),
      render: (r) => (r.isDefault ? <Badge color="green">Yes</Badge> : <span className="text-white/30">—</span>),
    },
  ];

  const modelRowActions = useMemo((): DataTableRowActions<AiModel> => ({
    extra: (r) => {
      const items: DataTableRowActionItem[] = [
        {
          label: "Test",
          onClick: () => modelTestMut.mutate(r.id),
        },
        {
          label: "Delete",
          variant: "danger",
          onClick: () =>
            confirm({
              title: "Delete model?",
              danger: true,
              confirmLabel: "Delete",
              action: () => modelDeleteMut.mutateAsync(r.id),
            }),
        },
      ];
      return items;
    },
  }), [confirm, modelDeleteMut, modelTestMut]);

  const kbRowActions = useMemo((): DataTableRowActions<KbArticle> => ({
    extra: (r) => [
      {
        label: "Delete",
        variant: "danger",
        onClick: () =>
          confirm({
            title: "Delete article?",
            danger: true,
            confirmLabel: "Delete",
            action: () => kbDeleteMut.mutateAsync(r.id),
          }),
      },
    ],
  }), [confirm, kbDeleteMut]);

  const kbColumns: Column<KbArticle>[] = [
    {
      key: "title",
      header: "Title",
      sortable: true,
      sortValue: (r) => r.title,
      render: (r) => <span className="font-medium">{r.title}</span>,
    },
    {
      key: "tags",
      header: "Tags",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.tags.join(", "),
      render: (r) => (
        <span className="text-xs text-white/50">{r.tags.length ? r.tags.join(", ") : "—"}</span>
      ),
    },
    {
      key: "active",
      header: "Active",
      sortable: true,
      sortValue: (r) => r.active,
      exportValue: (r) => (r.active ? "Yes" : "No"),
      render: (r) => (r.active ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge>),
    },
    {
      key: "updated",
      header: "Updated",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.updatedAt,
      exportValue: (r) => fmtDate(r.updatedAt),
      render: (r) => <span className="text-xs text-white/50">{fmtDate(r.updatedAt)}</span>,
    },
  ];

  return (
    <div className="page-scroll space-y-6">
      {dialog}

      <SettingsCard
        title="Model registry"
        description="Configure LLM and embedding providers for copilot and support."
        footer={
          <div className="flex justify-end">
            <Button size="sm" onClick={openModelCreate}>
              Add model
            </Button>
          </div>
        }
      >
        <DataTable
          columns={modelColumns}
          rows={models?.items}
          rowKey={(r) => r.id}
          loading={modelsLoading}
          exportFilename="ai-models"
          onRowClick={openModelEdit}
          rowActions={modelRowActions}
          empty={{ icon: Brain, title: "No models configured" }}
        />
      </SettingsCard>

      <SettingsCard
        title="Knowledge base"
        description="Articles indexed for RAG — edit content then reindex if needed."
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={reindexMut.isPending}
              onClick={() => reindexMut.mutate()}
            >
              {reindexMut.isPending ? "Queuing…" : "Reindex all"}
            </Button>
            <Button size="sm" onClick={openKbCreate}>
              Add article
            </Button>
          </div>
        }
      >
        <DataTable
          columns={kbColumns}
          rows={kb?.items}
          rowKey={(r) => r.id}
          loading={kbLoading}
          exportFilename="ai-knowledge-base"
          onRowClick={openKbEdit}
          rowActions={kbRowActions}
          empty={{ icon: BookOpen, title: "No knowledge articles" }}
        />
      </SettingsCard>

      <RightSidebarPanel
        open={modelEditor !== null}
        title={modelEditor === "new" ? "New model" : "Edit model"}
        onClose={closeModelEditor}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closeModelEditor}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={modelSaveMut.isPending}
              onClick={() =>
                modelSaveMut.mutate({
                  ...modelForm,
                  id: modelEditor !== "new" && modelEditor ? modelEditor.id : undefined,
                })
              }
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="sidebar-form">
          <Input label="Display name" value={modelForm.name} onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })} />
          <SearchSelect
            label="Purpose"
            options={MODEL_PURPOSE_OPTIONS}
            value={modelForm.purpose}
            onChange={(v) => setModelForm({ ...modelForm, purpose: v as ModelPurpose })}
          />
          <Input label="Base URL" value={modelForm.baseUrl} onChange={(e) => setModelForm({ ...modelForm, baseUrl: e.target.value })} />
          <Input
            label="API key"
            type="password"
            value={modelForm.apiKey}
            onChange={(e) => setModelForm({ ...modelForm, apiKey: e.target.value })}
            hint="Leave masked value unchanged when editing"
          />
          <Input label="Model ID" value={modelForm.modelId} onChange={(e) => setModelForm({ ...modelForm, modelId: e.target.value })} />
          <Input
            label="Temperature"
            type="number"
            min={0}
            max={2}
            step="0.1"
            value={modelForm.temperature}
            onChange={(e) => setModelForm({ ...modelForm, temperature: e.target.value })}
          />
          <Toggle
            label="Default for purpose"
            checked={modelForm.isDefault}
            onChange={(v) => setModelForm({ ...modelForm, isDefault: v })}
          />
        </div>
      </RightSidebarPanel>

      <RightSidebarPanel
        open={kbEditor !== null}
        wide
        title={kbEditor === "new" ? "New article" : "Edit article"}
        onClose={closeKbEditor}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closeKbEditor}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={kbSaveMut.isPending}
              onClick={() =>
                kbSaveMut.mutate({
                  ...kbForm,
                  id: kbEditor !== "new" && kbEditor ? kbEditor.id : undefined,
                })
              }
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="sidebar-form">
          <Input label="Title" value={kbForm.title} onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })} />
          <Input
            label="Tags"
            hint="Comma-separated"
            value={kbForm.tags}
            onChange={(e) => setKbForm({ ...kbForm, tags: e.target.value })}
          />
          <Textarea
            label="Content"
            rows={12}
            value={kbForm.content}
            onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })}
          />
          <Toggle label="Active" checked={kbForm.active} onChange={(v) => setKbForm({ ...kbForm, active: v })} />
        </div>
      </RightSidebarPanel>
    </div>
  );
}
